/**
 * TranscriptList — Chat message bubbles with auto-scroll.
 */
import { createElement, useRef, useEffect } from 'react';
import type { ChatTranscript } from '../../hooks/useCharlotteChat';
import type { VoiceTranscript } from '../../hooks/useCharlotteVoice';

type Transcript = ChatTranscript | VoiceTranscript;

interface TranscriptListProps {
  transcripts: Transcript[];
  /** Show "Charlotte is thinking..." indicator */
  thinking?: boolean;
}

function formatTime(timestamp: number): string {
  const d = new Date(timestamp);
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

export function TranscriptList({ transcripts, thinking }: TranscriptListProps) {
  const endRef = useRef<HTMLDivElement | null>(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcripts.length, thinking]);

  if (transcripts.length === 0 && !thinking) {
    return createElement(
      'div',
      {
        style: {
          display: 'flex',
          flexDirection: 'column' as const,
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          padding: '24px',
          color: 'var(--sw-text-secondary)',
          fontSize: '14px',
          textAlign: 'center' as const,
        },
      },
      createElement(
        'div',
        { style: { fontSize: '32px', marginBottom: '12px', opacity: 0.5 } },
        // Chat icon as SVG
        createElement(
          'svg',
          {
            width: 32,
            height: 32,
            viewBox: '0 0 24 24',
            fill: 'none',
            stroke: 'currentColor',
            strokeWidth: 1.5,
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
          },
          createElement('path', {
            d: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z',
          }),
        ),
      ),
      'Ask Charlotte anything about this product.',
    );
  }

  return createElement(
    'div',
    {
      style: {
        flex: 1,
        overflowY: 'auto' as const,
        padding: '8px 4px',
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
      },
      role: 'log',
      'aria-label': 'Chat messages',
      'aria-live': 'polite',
    },
    ...transcripts.map((t) => {
      const isUser = t.source === 'user';
      return createElement(
        'div',
        {
          key: t.id,
          style: {
            display: 'flex',
            justifyContent: isUser ? 'flex-end' : 'flex-start',
          },
        },
        createElement(
          'div',
          {
            style: {
              maxWidth: '85%',
              padding: '10px 14px',
              borderRadius: 'var(--sw-border-radius)',
              backgroundColor: isUser
                ? 'var(--sw-accent-primary)'
                : 'var(--sw-bg-surface)',
              color: isUser ? '#000' : 'var(--sw-text-primary)',
              fontSize: '14px',
              lineHeight: 1.5,
              wordBreak: 'break-word' as const,
            },
          },
          createElement('div', null, t.text),
          createElement(
            'div',
            {
              style: {
                fontSize: '11px',
                marginTop: '4px',
                opacity: 0.7,
                textAlign: isUser ? ('right' as const) : ('left' as const),
              },
            },
            formatTime(t.timestamp),
          ),
        ),
      );
    }),
    // Thinking indicator
    thinking
      ? createElement(
          'div',
          {
            key: 'thinking',
            style: {
              display: 'flex',
              justifyContent: 'flex-start',
            },
          },
          createElement(
            'div',
            {
              style: {
                padding: '10px 14px',
                borderRadius: 'var(--sw-border-radius)',
                backgroundColor: 'var(--sw-bg-surface)',
                color: 'var(--sw-text-secondary)',
                fontSize: '14px',
                fontStyle: 'italic',
              },
              role: 'status',
            },
            'Charlotte is thinking...',
          ),
        )
      : null,
    // Scroll anchor
    createElement('div', { ref: endRef }),
  );
}
